import * as React from "react";
import { createPortal } from "react-dom";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

type Option = { value: string; label: string };

// Componente para backdrop que cobre toda a altura do dialog
function BackdropOverlay({ backdropClassName }: { backdropClassName?: string }) {
  const [backdropStyle, setBackdropStyle] = React.useState<React.CSSProperties | null>(null);
  const backdropRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const updateRect = () => {
      const nodes = typeof window !== 'undefined' ? document.querySelectorAll('[data-caixa-dialog-content]') : undefined;
      const dialogEl = nodes && nodes.length ? (nodes[nodes.length - 1] as HTMLElement) : null;
      if (dialogEl) {
        const rect = dialogEl.getBoundingClientRect();
        
        // Encontrar o elemento que realmente contém o conteúdo scrollável
        // Procurar recursivamente por elementos com scroll ou max-height
        const findScrollContainer = (el: HTMLElement): HTMLElement => {
          const children = Array.from(el.children) as HTMLElement[];
          for (const child of children) {
            const style = window.getComputedStyle(child);
            const hasMaxHeight = style.maxHeight && style.maxHeight !== 'none';
            const hasOverflow = style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll';
            
            if (hasMaxHeight || hasOverflow || child.scrollHeight > child.clientHeight) {
              const nested = findScrollContainer(child);
              if (nested.scrollHeight > child.scrollHeight) {
                return nested;
              }
              return child;
            }
          }
          return el;
        };
        
        const scrollContainer = findScrollContainer(dialogEl);
        
        // Usar a altura total do conteúdo scrollável
        const contentHeight = Math.max(
          scrollContainer.scrollHeight,
          dialogEl.scrollHeight,
          rect.height
        );
        const contentWidth = Math.max(
          scrollContainer.scrollWidth,
          dialogEl.scrollWidth,
          rect.width
        );
        
        setBackdropStyle({
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          width: `${contentWidth}px`,
          height: `${contentHeight}px`,
        });
      }
    };

    updateRect();
    const resizeObserver = typeof window !== 'undefined' && window.ResizeObserver 
      ? new ResizeObserver(updateRect)
      : null;
    
    const nodes = typeof window !== 'undefined' ? document.querySelectorAll('[data-caixa-dialog-content]') : undefined;
    const dialogEl = nodes && nodes.length ? (nodes[nodes.length - 1] as HTMLElement) : null;
    if (dialogEl && resizeObserver) {
      resizeObserver.observe(dialogEl);
      // Observar também mudanças no conteúdo interno
      const mutationObserver = new MutationObserver(updateRect);
      mutationObserver.observe(dialogEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      
      // Atualizar quando houver scroll dentro do dialog
      const handleScroll = () => updateRect();
      dialogEl.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        dialogEl.removeEventListener('scroll', handleScroll);
      };
    }

    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, []);

  if (!backdropStyle) return null;

  return (
    <>
      {createPortal(
        <div className={cn("fixed inset-0 z-[45] bg-black/20 backdrop-blur-[2px] pointer-events-none", backdropClassName)} />,
        document.body
      )}
      {createPortal(
        <div 
          ref={backdropRef}
          className={cn("fixed z-[5] bg-black/20 backdrop-blur-[2px] pointer-events-none", backdropClassName)} 
          style={backdropStyle}
        />,
        document.body
      )}
    </>
  );
}

interface SearchableSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  triggerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  emptyText?: string;
  label?: React.ReactNode;
  renderAddModal?: (args: { open: boolean; onOpenChange: (open: boolean) => void; onCreated: (id: string) => void }) => React.ReactNode;
  addButtonPosition?: "left" | "right";
  valueLabel?: string;
  // Quando true (padrão), exibe backdrop com blur ao abrir o select
  withBackdrop?: boolean;
  // Classe extra para customizar o backdrop
  backdropClassName?: string;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Selecionar",
  triggerClassName,
  open,
  onOpenChange,
  emptyText = "Nenhum resultado",
  label,
  renderAddModal,
  addButtonPosition = "right",
  valueLabel,
  withBackdrop = true,
  backdropClassName,
}: SearchableSelectProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? valueLabel ?? placeholder;

  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setTriggerWidth(triggerRef.current?.offsetWidth ?? null);
    const obs = new ResizeObserver(() => setTriggerWidth(triggerRef.current?.offsetWidth ?? null));
    if (triggerRef.current) obs.observe(triggerRef.current);
    return () => obs.disconnect();
  }, [isOpen]);

  // Controle do modal de adição rápida (opcional)
  const [addOpen, setAddOpen] = React.useState(false);
  const handleCreated = (id: string) => {
    onChange(id);
    setAddOpen(false);
    setOpen(false);
  };

  // Sempre que abrir o modal de adição, garanta que o popover esteja fechado
  React.useEffect(() => {
    if (addOpen) setOpen(false);
  }, [addOpen, setOpen]);

  return (
    <Popover.Root
      open={isOpen && !addOpen}
      onOpenChange={(next) => {
        if (addOpen) return; // ignora abertura enquanto o modal de adição estiver aberto
        setOpen(next);
      }}
    >
      <div className={cn("w-full", isOpen ? "relative z-[4000]" : undefined)}> 
        {label && (
          <div className="mb-2 text-sm font-medium text-foreground">{label}</div>
        )}
        <div className="flex items-center gap-2 w-full">
          {renderAddModal && addButtonPosition === "left" && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={() => { setOpen(false); setAddOpen(true); }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
          <Popover.Trigger asChild>
            <button
              ref={triggerRef}
              type="button"
              data-searchable-trigger
              className={cn(
                "relative flex h-10 flex-1 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                triggerClassName,
              )}
            >
              <span className={cn(!selected && !valueLabel && "text-muted-foreground line-clamp-1")}> 
                {displayLabel}
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </button>
          </Popover.Trigger>
          {renderAddModal && addButtonPosition === "right" && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={() => { setOpen(false); setAddOpen(true); }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {withBackdrop && (isOpen && !addOpen) && (
        <BackdropOverlay backdropClassName={backdropClassName} />
      )}

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          avoidCollisions={false}
          className="z-[4001] rounded-md border bg-popover p-0 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{ width: triggerWidth ?? undefined, maxWidth: "90vw" }}
        >
          <Command>
            <CommandInput placeholder="Digite para filtrar..." />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </Popover.Content>
      </Popover.Portal>

      {renderAddModal && renderAddModal({ open: addOpen, onOpenChange: setAddOpen, onCreated: handleCreated })}
    </Popover.Root>
  );
}
